import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../contexts/ToastContext';
import { neo4jService } from '../../services/neo4jService';
import { Star } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import './Profile.css';

const Profile = () => {
  const { currentUser, login, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Initialize form data with current user data
    setFormData({
      name: currentUser.name || '',
      email: currentUser.email || ''
    });

    async function fetchProfile() {
      try {
        const profileData = await neo4jService.getCustomerProfile(currentUser.id);
        if (profileData) {
          setProfile(profileData);
          setFormData({
            name: profileData.name || '',
            email: profileData.email || ''
          });
        } else {
          setProfile({
            id: currentUser.id,
            name: currentUser.name || '',
            email: currentUser.email || '',
            ratings: []
          });
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        toast.error('Failed to load profile');
        setProfile({
          id: currentUser.id,
          name: currentUser.name || '',
          email: currentUser.email || '',
          ratings: []
        });
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updated = await neo4jService.updateCustomerProfile(currentUser.id, formData);
      login(updated); // Update auth context
      setProfile(prev => ({
        ...prev,
        ...updated
      }));
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!currentUser) {
    return null;
  }

  const ratings = Array.isArray(profile?.ratings)
    ? profile.ratings.filter(rating => rating && rating.rating != null && rating.productId)
    : [];

  return (
    <div className="profile-container">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <h1>My Profile</h1>
      </div>
      {editing ? (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled
            />
          </div>
          <div className="button-group">
            <button type="submit" className="save-button">Save</button>
            <button type="button" onClick={() => setEditing(false)} className="cancel-button">Cancel</button>
          </div>
        </form>
      ) : (
        <div className="profile-info">
          <div className="info-group">
            <label>Name</label>
            <p>{formData.name || 'Not set'}</p>
          </div>
          <div className="info-group">
            <label>Email</label>
            <p>{formData.email}</p>
          </div>
          <button onClick={() => setEditing(true)} className="edit-button">Edit Profile</button>
        </div>
      )}
      {ratings.length > 0 && (
        <div className="ratings-section">
          <h3>My Reviews</h3>
          <div className="ratings-list">
            {ratings.map((rating, index) => (
              <div key={index} className="rating-item">
                <div className="rating-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`star ${i < rating.rating ? 'filled' : ''}`}
                      size={16}
                    />
                  ))}
                </div>
                <span className="rating-date">
                  {new Date(rating.timestamp).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <button className="logout-button" onClick={handleLogout}>
        Logout
      </button>
    </div>
  );
};

export default Profile;
